export const AUTH_GENERATION_CHANNEL_NAME = 'nucleai-auth-session-generation';
export const AUTH_GENERATION_STORAGE_EVENT_KEY = 'authSessionGenerationNotice';

export interface GenerationNotice {
  type: 'generation-updated';
  generationId: string | null;
}

export type GenerationNoticeListener = (notice: GenerationNotice) => void;

const isGenerationNotice = (value: unknown): value is GenerationNotice => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const notice = value as GenerationNotice;
  return (
    notice.type === 'generation-updated' && (notice.generationId === null || typeof notice.generationId === 'string')
  );
};

export const parseGenerationNotice = (value: unknown): GenerationNotice | null => {
  if (typeof value === 'string') {
    try {
      return parseGenerationNotice(JSON.parse(value));
    } catch {
      return null;
    }
  }

  if (!isGenerationNotice(value)) {
    return null;
  }

  return {
    type: 'generation-updated',
    generationId: value.generationId,
  };
};

export const createGenerationNotifier = (
  channelFactory: (() => BroadcastChannel | null) | null = () =>
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(AUTH_GENERATION_CHANNEL_NAME),
  storage: Pick<Storage, 'setItem'> | null = typeof window === 'undefined' ? null : window.localStorage
) => {
  let channel: BroadcastChannel | null = null;

  const getChannel = () => {
    if (channelFactory === null) {
      return null;
    }
    if (!channel) {
      try {
        channel = channelFactory();
      } catch {
        channel = null;
      }
    }
    return channel;
  };

  const notifyGenerationUpdated = (generationId: string | null) => {
    const notice: GenerationNotice = { type: 'generation-updated', generationId };
    try {
      getChannel()?.postMessage(notice);
    } catch {
      // The persisted generation remains authoritative when a notification transport fails.
    }

    try {
      storage?.setItem(AUTH_GENERATION_STORAGE_EVENT_KEY, JSON.stringify({ ...notice, at: Date.now() }));
    } catch {
      // Storage events are a best-effort fallback for tabs without BroadcastChannel.
    }
  };

  const subscribeToGenerationNotifications = (listener: GenerationNoticeListener): (() => void) => {
    const currentChannel = getChannel();
    const handleMessage = (event: MessageEvent) => {
      const notice = parseGenerationNotice(event.data);
      if (notice) {
        listener(notice);
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_GENERATION_STORAGE_EVENT_KEY || !event.newValue) {
        return;
      }
      const notice = parseGenerationNotice(event.newValue);
      if (notice) {
        listener(notice);
      }
    };

    currentChannel?.addEventListener('message', handleMessage);
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorage);
    }

    return () => {
      currentChannel?.removeEventListener('message', handleMessage);
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', handleStorage);
      }
    };
  };

  const close = () => {
    channel?.close();
    channel = null;
  };

  return {
    notifyGenerationUpdated,
    subscribeToGenerationNotifications,
    close,
  };
};
